"use client";
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { MapPin, Clock, Users, Heart, ShieldCheck, AlertTriangle, HeartHandshake, SearchX } from 'lucide-react';
import { FilterState } from './CategoryFilter';

interface Cause {
  id: string;
  title: string;
  category: string;
  urgency: string;
  location: string;
  raised: number;
  goal: number;
  donors: number;
  daysLeft: number;
  image: string;
  imageAlt: string;
  excerpt: string;
  verified: boolean;
  giftAidEligible: boolean;
  charity: string;
}

const causes: Cause[] = [];

const urgencyOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

interface CauseGridProps {
  filters: FilterState;
}

export default function CauseGrid({ filters }: CauseGridProps) {
  const [showAll, setShowAll] = useState(false);

  if (causes.length === 0) {
    return (
      <div className="mb-12">
        <div className="card p-16 text-center">
          <HeartHandshake size={40} className="mx-auto mb-4 text-teal-300" />
          <h3 className="font-display text-lg font-700 text-gray-700 mb-2">No active causes yet</h3>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            Verified causes will appear here once applications have been reviewed and approved. Check back soon.
          </p>
        </div>
      </div>
    );
  }

  // Apply filters
  let filtered = causes.filter((cause) => {
    const searchLower = filters.search.toLowerCase();
    const matchesSearch =
      !filters.search ||
      cause.title.toLowerCase().includes(searchLower) ||
      cause.charity.toLowerCase().includes(searchLower) ||
      cause.excerpt.toLowerCase().includes(searchLower);

    const matchesCategory = filters.category === 'all' || cause.category === filters.category;
    const matchesUrgency = filters.urgency === 'all' || cause.urgency === filters.urgency;
    const matchesLocation = filters.location === 'All UK' || cause.location.toLowerCase().includes(filters.location.toLowerCase());

    return matchesSearch && matchesCategory && matchesUrgency && matchesLocation;
  });

  // Apply sort
  filtered = [...filtered].sort((a, b) => {
    switch (filters.sort) {
      case 'urgent':
        return (urgencyOrder[a.urgency] ?? 9) - (urgencyOrder[b.urgency] ?? 9);
      case 'goal':
        return (b.raised / b.goal) - (a.raised / a.goal);
      case 'recent':
        return a.daysLeft - b.daysLeft;
      case 'donors':
        return b.donors - a.donors;
      default:
        return 0;
    }
  });

  const displayedCauses = showAll ? filtered : filtered.slice(0, 9);

  if (filtered.length === 0) {
    return (
      <div className="mb-12">
        <div className="card p-16 text-center">
          <SearchX size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="font-display text-lg font-700 text-gray-700 mb-2">No causes match your filters</h3>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            Try adjusting your search terms, category, location, or urgency level to find more causes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-12">
      <p className="text-sm text-gray-500 mb-4">
        Showing <span className="font-semibold text-gray-700">{filtered.length}</span> cause{filtered.length !== 1 ? 's' : ''}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
        {displayedCauses?.map((cause) => {
          const pct = Math.round(cause?.raised / cause?.goal * 100);
          return (
            <div
              key={cause?.id}
              className="card overflow-hidden hover:shadow-elevated transition-all duration-200 group flex flex-col">
              <div className="relative h-48 overflow-hidden bg-gray-100">
                <AppImage
                  src={cause?.image}
                  alt={cause?.imageAlt}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
                <div className="absolute top-3 left-3 flex gap-2">
                  {cause?.urgency === 'critical' &&
                    <span className="badge-urgent flex items-center gap-1">
                      <AlertTriangle size={11} />
                      Critical
                    </span>
                  }
                  {cause?.urgency === 'high' &&
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-100">
                      High Priority
                    </span>
                  }
                  {cause?.category === 'child' &&
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-100">
                      Child
                    </span>
                  }
                  {cause?.category === 'family' &&
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                      Family
                    </span>
                  }
                </div>
                {cause?.verified &&
                  <div className="absolute top-3 right-3">
                    <span className="badge-verified">
                      <ShieldCheck size={11} />
                      Verified
                    </span>
                  </div>
                }
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-1.5 mb-2">
                  <MapPin size={12} className="text-gray-400" />
                  <span className="text-xs text-gray-500">{cause?.location}</span>
                  <span className="text-gray-300 mx-1">·</span>
                  <span className="text-xs text-gray-500">{cause?.charity}</span>
                </div>
                <h3 className="font-display text-sm font-700 text-gray-900 leading-snug mb-2 line-clamp-2">
                  {cause?.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4 line-clamp-2 flex-1">
                  {cause?.excerpt}
                </p>
                <div className="mt-auto">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="font-mono font-bold text-teal-600">£{cause?.raised?.toLocaleString()}</span>
                    <span className="text-gray-400">of £{cause?.goal?.toLocaleString()}</span>
                  </div>
                  <div className="progress-bar mb-3">
                    <div
                      className={`progress-fill ${pct >= 90 ? 'bg-gradient-to-r from-green-500 to-green-600' : ''}`}
                      style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Users size={12} />
                      {cause?.donors} donors
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {cause?.daysLeft}d left
                    </span>
                    <span className="font-semibold text-teal-600">{pct}% funded</span>
                  </div>
                  <div className="flex gap-2">
                    <Link
                      href="/sign-up-login-screen"
                      className="flex-1 text-center py-2.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-all duration-150 active:scale-95">
                      Donate Now
                    </Link>
                    <button className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 border border-gray-200 rounded-xl transition-all duration-150">
                      <Heart size={16} />
                    </button>
                  </div>
                  {cause?.giftAidEligible &&
                    <p className="text-center text-xs text-teal-600 mt-2 font-medium">
                      + 25% Gift Aid available for UK taxpayers
                    </p>
                  }
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {!showAll && filtered.length > 9 &&
        <div className="text-center mt-10">
          <button
            onClick={() => setShowAll(true)}
            className="btn-secondary px-8 py-3 text-sm">
            Load More Causes
          </button>
        </div>
      }
    </div>
  );
}